import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import './Marketplace.css';

const MarketplaceForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    price: '',
    condition_id: '',
    category_id: '',
    location: '',
    payment_methods: {
      cash: false,
      bank_transfer: false,
      paypal: false
    },
    show_phone_to: 'R', // Default to "registered users only"
    // Other fields...
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '', // Neues Feld für die Kategorie-ID
    price: '',
    is_negotiable: false,
    shipping_available: false,
    pickup_preferred: false,
    weight: '', // Neues Feld für Gewicht
    city: '',
    zip: '',
    country_code: '',
    is_private: false,
    show_phone_to: 'N', // N=None, R=Registered, A=All
    payment_methods: {
      cash: false,
      bank_transfer: false,
      paypal: false
    }
  });

  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Add this state to track character count (near other useState declarations):
  const [descriptionChars, setDescriptionChars] = useState(0);
  const MAX_DESCRIPTION_LENGTH = 1600;

  // 1. Add the missing state variable with your other useState calls at the top
  const [existingImages, setExistingImages] = useState([]);

  // Neue Funktion zum Laden der Kategorien
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        
        const response = await fetch('/api/marketplace/categories');
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Kategorien');
        }
        
        const data = await response.json();
        setCategories(data);
        
        // Hauptkategorien (ohne parent_id) und Unterkategorien trennen
        const mainCats = data.filter(cat => !cat.parent_id);
        const subCats = data.filter(cat => cat.parent_id);
        
        setMainCategories(mainCats);
        setSubCategories(subCats);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError('Kategorien konnten nicht geladen werden: ' + error.message);
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Make sure the useEffect for fetching listing data in edit mode is correct:
  useEffect(() => {
    // Set page title based on mode
    document.title = isEditing ? 'Angebot bearbeiten' : 'Neues Angebot erstellen';
    
    // If in edit mode, fetch the listing data
    if (isEditing && id) {
      const fetchListing = async () => {
        try {
          setLoading(true);
          const response = await fetch(`/api/marketplace/${id}`);
          
          if (!response.ok) {
            throw new Error('Angebot konnte nicht geladen werden');
          }
          
          const data = await response.json();
          console.log('Fetched listing for edit:', data);
          
          // Parse payment methods
          let paymentMethodsObj = { cash: false, bank_transfer: false, paypal: false };
          try {
            if (typeof data.payment_methods === 'string') {
              paymentMethodsObj = JSON.parse(data.payment_methods);
            } else if (typeof data.payment_methods === 'object') {
              paymentMethodsObj = data.payment_methods;
            }
          } catch (e) {
            console.error('Error parsing payment methods:', e);
          }
          
          // Set form values
          setFormValues({
            title: data.title || '',
            description: data.description || '',
            price: data.price ? parseFloat(data.price) : '',
            condition_id: data.condition_id ? data.condition_id.toString() : '',
            category_id: data.category_id ? data.category_id.toString() : '',
            location: data.location || '',
            payment_methods: paymentMethodsObj,
            show_phone_to: data.show_phone_to || 'N',
            weight: data.weight || '',
            is_negotiable: data.is_negotiable || false
          });
          
          // Set existing images
          if (data.images && data.images.length > 0) {
            setExistingImages(data.images);
          }
          
          setLoading(false);

          // After setting form values, also set the character count
          if (data && data.description) {
            setDescriptionChars(data.description.length);
          }
        } catch (error) {
          console.error('Error fetching listing:', error);
          setError(error.message);
          setLoading(false);
        }
      };
      
      fetchListing();
    }
  }, [isEditing, id]);

  // 2. Replace the useEffect for fetching listing data with this complete version
  useEffect(() => {
    if (isEditing && id) {
      const fetchListing = async () => {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          
          const response = await fetch(`/api/marketplace/${id}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (!response.ok) {
            throw new Error('Fehler beim Laden des Angebots');
          }
          
          const listing = await response.json();
          console.log("Loaded listing for editing:", listing);
          
          // Set form values from listing data
          setFormValues({
            title: listing.title || '',
            description: listing.description || '',
            price: listing.price || '',
            category_id: listing.category_id || '',
            condition_id: listing.condition_id || '',
            is_negotiable: listing.is_negotiable === 'Y',
            shipping_available: listing.shipping_available === 'Y',
            pickup_preferred: listing.pickup_preferred === 'Y',
            weight: listing.weight || '',
            city: listing.city || '',
            zip: listing.zip || '',
            country_code: listing.country_code || 'DE',
            is_private: listing.is_private === 'Y',
            show_phone_to: listing.show_phone_to || 'N',
            payment_methods: typeof listing.payment_methods === 'string' 
              ? JSON.parse(listing.payment_methods)
              : (listing.payment_methods || { cash: false, bank_transfer: false, paypal: false })
          });
          
          // IMPORTANT: Set formData for title and other critical fields
          setFormData({
            title: listing.title || '',
            description: listing.description || '',
            price: listing.price || '',
            category_id: listing.category_id || '',
            city: listing.city || '',
            zip: listing.zip || '',
            country_code: listing.country_code || 'DE',
            show_phone_to: listing.show_phone_to || 'N',
            is_private: listing.is_private === 'Y'
          });
          
          // Handle existing images
          if (listing.images && listing.images.length > 0) {
            console.log("Processing existing images:", listing.images);
            const imagePreviews = [];
            
            // Handle different formats of image data
            for (let i = 0; i < listing.images.length; i++) {
              const img = listing.images[i];
              let imagePath;
              let imageId;
              
              if (typeof img === 'string') {
                // Handle plain string path
                imagePath = img;
                imageId = i.toString();
              } else if (img && typeof img === 'object') {
                // Handle object format with image_path or path
                imagePath = img.image_path || img.path || '';
                imageId = img.id || i.toString();
              }
              
              if (imagePath) {
                imagePreviews.push({
                  id: imageId.toString(),
                  path: imagePath,
                  isExisting: true
                });
              }
            }
            
            console.log("Processed image previews:", imagePreviews);
            setExistingImages(imagePreviews);
          }
          
          // Select the category in the DOM after a short delay to ensure the options are loaded
          setTimeout(() => {
            const categorySelect = document.getElementById('category_id');
            if (categorySelect && listing.category_id) {
              categorySelect.value = listing.category_id;
            }
          }, 500);
          
        } catch (error) {
          console.error('Error fetching listing:', error);
          setError('Fehler beim Laden des Angebots');
        } finally {
          setLoading(false);
        }
      };
      
      fetchListing();
    }
  }, [id, isEditing]);

  // Zeige Vorschau für neu ausgewählte Bilder
  useEffect(() => {
    const previews = [];
    for (let i = 0; i < newImages.length; i++) {
      previews.push(URL.createObjectURL(newImages[i]));
    }
    setPreviewImages(previews);

    // Bereinige die Vorschaubilder, wenn die Komponente unmounted
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview));
    };
  }, [newImages]);

  // Überprüfe Authentifizierung
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  // Fülle Profilinformationen aus (für neue Angebote)
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (isEditing) return;

      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch('/api/user/profile', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) return;

        const data = await response.json();
        
        setFormData(prevState => ({
          ...prevState,
          city: data.city || '',
          zip: data.zip || '',
          country_code: data.country_code || ''
        }));
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchUserProfile();
  }, [isEditing]);

  // Update handleInputChange to handle nested payment_methods
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('payment_methods.')) {
      const method = name.split('.')[1];
      setFormData({
        ...formData,
        payment_methods: {
          ...formData.payment_methods,
          [method]: checked
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
  };

  // Add this function to handle description changes:
  const handleDescriptionChange = (e) => {
    const text = e.target.value;
    if (text.length <= MAX_DESCRIPTION_LENGTH) {
      // Update form normally
      setFormValues({
        ...formValues,
        description: text
      });
      setDescriptionChars(text.length);
    } else {
      // Truncate to max length
      setFormValues({
        ...formValues,
        description: text.substring(0, MAX_DESCRIPTION_LENGTH)
      });
      setDescriptionChars(MAX_DESCRIPTION_LENGTH);
      
      // Show alert
      alert(`Die Beschreibung ist auf ${MAX_DESCRIPTION_LENGTH} Zeichen begrenzt.`);
    }
  };

  // Updated handleImageChange function
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Prüfe, ob zu viele Bilder ausgewählt wurden
    if (files.length + images.length > 5) {
      setError('Sie können maximal 5 Bilder hochladen.');
      return;
    }
    
    // Prüfe Dateitypen
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Nur JPG, PNG und WebP Dateien sind erlaubt.');
      return;
    }
    
    // Add to existing newImages instead of replacing
    setNewImages(prevImages => [...prevImages, ...files]);
    setError(null);
  };

  const handleDeleteImage = async (imageId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`/api/marketplace/${id}/images/${imageId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Fehler beim Löschen des Bildes');
      }
      
      // Bild aus der Liste entfernen
      setImages(images.filter(img => img.id !== imageId));
      setMessage('Bild erfolgreich gelöscht');
      
      // Nachricht nach 3 Sekunden ausblenden
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error deleting image:', error);
      setError(error.message);
    }
  };

  // 5. Update form submission to track removed images
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Sie müssen angemeldet sein, um ein Angebot zu erstellen');
      }
      
      // Create FormData object
      const formDataObj = new FormData();
      
      // IMPORTANT: First add new images - this must come BEFORE anything else
      if (newImages.length > 0) {
        newImages.forEach(image => {
          formDataObj.append('images', image);
        });
      }
      
      // Get values from both states, with formData taking precedence for title, category and location
      const titleValue = document.getElementById('title').value || formData.title || '';
      const descriptionValue = document.getElementById('description').value || formValues.description || '';
      const priceValue = document.getElementById('price').value || formValues.price || '';
      
      // Get selected category directly from the select element
      const categorySelect = document.getElementById('category_id');
      const categoryValue = categorySelect ? categorySelect.value : (formData.category_id || formValues.category_id || '');
      
      console.log('Selected category:', categoryValue);
      
      // Add form fields to FormData
      formDataObj.append('title', titleValue);
      formDataObj.append('description', descriptionValue);
      formDataObj.append('price', priceValue);
      
      // Always include category when available
      if (categoryValue) {
        formDataObj.append('category_id', categoryValue);
      }
      
      if (formValues.condition_id) {
        formDataObj.append('condition_id', formValues.condition_id.toString());
      }
      
      // Boolean fields - use formValues for these
      formDataObj.append('is_negotiable', formValues.is_negotiable ? 'Y' : 'N');
      formDataObj.append('shipping_available', formValues.shipping_available ? 'Y' : 'N');
      formDataObj.append('pickup_preferred', formValues.pickup_preferred ? 'Y' : 'N');
      formDataObj.append('is_private', formData.is_private ? 'Y' : 'N');
      
      // Location - use formData for these
      formDataObj.append('city', formData.city || '');
      formDataObj.append('zip', formData.zip || '');
      formDataObj.append('country_code', formData.country_code || 'DE');
      
      // Other fields
      if (formValues.weight) {
        formDataObj.append('weight', formValues.weight.toString());
      }
      
      // Use formData for show_phone_to
      formDataObj.append('show_phone_to', formData.show_phone_to || 'N');
      
      // Payment methods
      const paymentMethods = formValues.payment_methods || { cash: false, bank_transfer: false, paypal: false };
      formDataObj.append('payment_methods', JSON.stringify(paymentMethods));
      
      // THEN add existing image IDs that should be kept WITH their positions
      if (isEditing && existingImages.length > 0) {
        // Create an array of {id, position} objects to track order
        const keptImagesWithPositions = existingImages.map((img, index) => {
          const rawId = typeof img.id === 'string' 
            ? (img.id.startsWith('existing-') ? img.id.replace('existing-', '') : img.id) 
            : img.id;
            
          return {
            id: rawId,
            position: index
          };
        });
        
        // Send both simple ID array and the positioned info
        const keptImageIds = keptImagesWithPositions.map(item => item.id);
        formDataObj.append('keptImageIds', JSON.stringify(keptImageIds));
        formDataObj.append('imagePositions', JSON.stringify(keptImagesWithPositions));
        
        // Debug
        console.log("Saving images with positions:", keptImagesWithPositions);
      }
      
      // Debug what's being sent
      console.log('FormData contents:');
      for (const pair of formDataObj.entries()) {
        console.log(pair[0] + ': ' + (pair[0] === 'images' ? 'Image file' : pair[1]));
      }
      
      // API endpoint
      const url = isEditing ? `/api/marketplace/${id}` : '/api/marketplace';
      console.log(`Sending ${isEditing ? 'PUT' : 'POST'} request to ${url}`);
      
      // Make the API request
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataObj
      });
      
      // Handle the response
      if (!response.ok) {
        const responseText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || 'Ein Fehler ist aufgetreten';
        } catch (e) {
          errorMessage = 'Ein unbekannter Fehler ist aufgetreten';
        }
        throw new Error(errorMessage);
      }
      
      const responseData = await response.json();
      
      // Redirect to listing page
      if (isEditing) {
        navigate(`/marketplace/${id}`);
      } else {
        navigate(`/marketplace/${responseData.id}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.message || 'Ein unbekannter Fehler ist aufgetreten');
    } finally {
      setSubmitting(false);
    }
  };

  // Add this near the handleSubmit function:

  useEffect(() => {
    // If we've been submitting for more than 20 seconds, something is wrong
    let submissionTimeout;
    
    if (submitting) {
      submissionTimeout = setTimeout(() => {
        console.error('Form submission timeout - taking too long');
        setSubmitting(false);
        setError('Die Anfrage dauert zu lange. Bitte versuchen Sie es erneut.');
      }, 20000); // 20 seconds timeout
    }
    
    return () => {
      if (submissionTimeout) {
        clearTimeout(submissionTimeout);
      }
    };
  }, [submitting]);

  // Add function to remove preview images
  const removePreviewImage = (index) => {
    setNewImages(prevImages => {
      const updatedImages = [...prevImages];
      updatedImages.splice(index, 1);
      return updatedImages;
    });
  };

  useEffect(() => {
    // Existing form initialization code...
    
    // Initialize character count
    setDescriptionChars(formValues.description?.length || 0);
  }, [formValues.description]);

  // Add or update the payment method handler
  const handlePaymentMethodChange = (e) => {
    const { name, checked } = e.target;
    const methodName = name.split('.')[1]; // Extract method name from "payment_methods.cash"
    
    setFormValues({
      ...formValues,
      payment_methods: {
        ...formValues.payment_methods,
        [methodName]: checked
      }
    });
  };

  // Add this synchronization effect
  useEffect(() => {
    if (formData.title !== formValues.title || 
        formData.description !== formValues.description ||
        formData.price !== formValues.price) {
      // Keep formValues in sync with formData for critical fields
      setFormValues(prevValues => ({
        ...prevValues,
        title: formData.title,
        description: formData.description,
        price: formData.price
      }));
    }
  }, [formData.title, formData.description, formData.price]);

  // 3. Add functions to handle existing images
  const handleRemoveExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  // 4. Update the image preview rendering in your JSX
  const renderImagePreviews = () => {
    return (
      <div className="image-previews">
        <h3>Bilder für das Angebot</h3>
        <div className="image-order-instructions">
          <span className="material-symbols-outlined">info</span>
          <p>Das erste Bild wird als Hauptbild in der Übersicht angezeigt. Zum Ändern der Reihenfolge ziehen Sie die Bilder per Drag & Drop.</p>
        </div>
        
        <div className="sortable-image-gallery" id="sortable-images">
          {existingImages.map((image, index) => (
            <div 
              key={`existing-${image.id}`} 
              className="image-preview"
              data-image-id={image.id}
              draggable="true"
              onDragStart={(e) => handleDragStart(e, index, 'existing')}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index, 'existing')}
            >
              <div className="image-position">{index + 1}</div>
              <div className="image-container">
                <img 
                  src={image.path} 
                  alt={`Vorschau ${index + 1}`}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-image.png'; 
                  }}
                />
              </div>
              <div className="image-controls">
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={() => handleRemoveExistingImage(index)}
                  title="Bild entfernen"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>
          ))}
          
          {newImages.map((image, index) => (
            <div 
              key={`new-${index}`} 
              className="image-preview"
              draggable="true"
              onDragStart={(e) => handleDragStart(e, index, 'new')}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index, 'new')}
            >
              <div className="image-position">{existingImages.length + index + 1}</div>
              <div className="image-container">
                <img 
                  src={URL.createObjectURL(image)} 
                  alt={`Vorschau ${index + 1}`}
                />
              </div>
              <div className="image-controls">
                <button
                  type="button"
                  className="remove-image-button"
                  onClick={() => removePreviewImage(index)}
                  title="Bild entfernen"
                >
                  <span className="material-symbols-outlined">delete</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Move these drag and drop functions INSIDE the component
  const handleDragStart = (e, index, type) => {
    e.dataTransfer.setData('index', index);
    e.dataTransfer.setData('type', type);
    e.target.classList.add('dragging');
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetIndex, targetType) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('index'));
    const sourceType = e.dataTransfer.getData('type');
    
    if (sourceType === targetType) {
      // Same type of images (existing or new)
      if (sourceType === 'existing') {
        const updatedImages = [...existingImages];
        const [movedImage] = updatedImages.splice(sourceIndex, 1);
        updatedImages.splice(targetIndex, 0, movedImage);
        setExistingImages(updatedImages);
      } else {
        const updatedImages = [...newImages];
        const [movedImage] = updatedImages.splice(sourceIndex, 1);
        updatedImages.splice(targetIndex, 0, movedImage);
        setNewImages(updatedImages);
      }
    } else {
      // Moving between existing and new images
      if (sourceType === 'existing' && targetType === 'new') {
        // This is more complex and requires converting an existing image to a new one
        setError('Umordnung zwischen vorhandenen und neuen Bildern ist nicht möglich');
      } else if (sourceType === 'new' && targetType === 'existing') {
        setError('Umordnung zwischen neuen und vorhandenen Bildern ist nicht möglich');
      }
    }
  };

  if (loading) {
    return (
      <div className="marketplace-form-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Lade Formular...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="marketplace-form-container">
      <div className="form-header">
        <h1>{isEditing ? 'Angebot bearbeiten' : 'Neues Angebot erstellen'}</h1>
        <Link to={isEditing ? `/marketplace/${id}` : '/marketplace'} className="cancel-link">
          <span className="material-symbols-outlined">arrow_back</span>
          {isEditing ? 'Zurück zum Angebot' : 'Zurück zum Gebrauchtmarkt'}
        </Link>
      </div>
      
      {error && (
        <div className="error-message">
          <span className="material-symbols-outlined">error</span>
          <p>{error}</p>
        </div>
      )}
      
      {message && (
        <div className="success-message">
          <span className="material-symbols-outlined">check_circle</span>
          <p>{message}</p>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="marketplace-form">
        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">info</span>
            Grundinformationen
          </h2>
          
          <div className="form-group">
            <label htmlFor="title">Titel <span className="required">*</span></label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              placeholder="z.B. Dynaudio Contour 30i in Walnuss"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="category_id">Kategorie <span className="required">*</span></label>
            {loadingCategories ? (
              <div className="loading-spinner small"></div>
            ) : (
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleInputChange}
                required
              >
                <option value="">-- Kategorie wählen --</option>
                
                {mainCategories.map(category => (
                  <optgroup key={category.id} label={category.name}>
                    {/* Hauptkategorien können auch direkt ausgewählt werden */}
                    <option value={category.id}>{category.name} (Allgemein)</option>
                    
                    {/* Unterkategorien der aktuellen Hauptkategorie */}
                    {subCategories
                      .filter(subCat => subCat.parent_id === category.id)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(subCat => (
                        <option key={subCat.id} value={subCat.id}>
                          {subCat.name}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            )}
            <small className="form-hint">
              Wählen Sie die passende Kategorie für Ihr Angebot
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="description">
              Beschreibung
              <span className={`char-count ${descriptionChars === MAX_DESCRIPTION_LENGTH ? 'char-count-limit' : ''}`}>
                {descriptionChars}/{MAX_DESCRIPTION_LENGTH}
              </span>
            </label>
            <textarea
              id="description"
              name="description"
              value={formValues.description}
              onChange={handleDescriptionChange}
              required
              className="form-control"
              placeholder="HINWEIS: dieser Markt ist für hochwertige Audio-Komponenten gedacht und nicht als Flohmarkt! Bitte, nutzen Sie hierfür EBay oder Kleinanzeigen, danke! VON PRIVATEN und FÜR Hifi-Begeisterte, vertrauensvolle Angebote!"
              rows="6"
            />
            {descriptionChars === MAX_DESCRIPTION_LENGTH && (
              <div className="char-limit-warning">
                Sie haben das Zeichenlimit erreicht.
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label htmlFor="price">Preis (€) <span className="required">*</span></label>
            <div className="price-input-container">
              <input
                type="text" // Changed from number to text for more flexible input
                id="price"
                name="price"
                value={formValues.price}
                onChange={(e) => {
                  // Direct handler to ensure immediate updates
                  // Allow only numbers and a single decimal point
                  const value = e.target.value;
                  if (value === '' || /^[0-9]*\.?[0-9]*$/.test(value)) {
                    setFormValues({...formValues, price: value});
                  }
                }}
                required
                placeholder="0.00"
                className="price-input"
              />
            </div>
            
            <div className="price-options">
              <label className={`option-button ${formValues.is_negotiable ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="price_type"
                  checked={formValues.is_negotiable}
                  onChange={() => setFormValues({...formValues, is_negotiable: true})}
                  style={{display: 'none'}} // Hide the actual radio button
                />
                <span className="material-symbols-outlined">
                  published_with_changes
                </span>
                Verhandelbar (VB)
              </label>
              
              <label className={`option-button ${!formValues.is_negotiable ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="price_type"
                  checked={!formValues.is_negotiable}
                  onChange={() => setFormValues({...formValues, is_negotiable: false})}
                  style={{display: 'none'}} // Hide the actual radio button
                />
                <span className="material-symbols-outlined">
                  price_check
                </span>
                Festpreis (FP)
              </label>
            </div>
          </div>

          <div className="form-group form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="is_negotiable"
              name="is_negotiable"
              checked={formValues.is_negotiable}
              onChange={(e) => setFormValues({...formValues, is_negotiable: e.target.checked})}
            />
            <label className="form-check-label" htmlFor="is_negotiable">
              Verhandelbar (VB)
            </label>
          </div>
        </div>
        
        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">local_shipping</span>
            Versandoptionen
          </h2>
          
          <div className="shipping-status-group">
            <label className={`shipping-option ${formValues.shipping_available ? 'active' : ''}`}>
              <input
                type="checkbox"
                name="shipping_available"
                checked={formValues.shipping_available}
                onChange={(e) => setFormValues({...formValues, shipping_available: e.target.checked})}
                style={{display: 'none'}}
              />
              <span className="material-symbols-outlined">local_shipping</span>
              <p>Versand möglich</p>
              <span className="shipping-hint">Käufer zahlt Versandkosten</span>
            </label>
            
            <label className={`shipping-option ${formValues.pickup_preferred ? 'active' : ''}`}>
              <input
                type="checkbox"
                name="pickup_preferred"
                checked={formValues.pickup_preferred}
                onChange={(e) => setFormValues({...formValues, pickup_preferred: e.target.checked})}
                style={{display: 'none'}}
              />
              <span className="material-symbols-outlined">store</span>
              <p>Abholung bevorzugt</p>
              <span className="shipping-hint">Bevorzugt persönliche Übergabe</span>
            </label>
          </div>

          {/* Weight field */}
          <div className="form-group">
            <label htmlFor="weight">Gewicht (kg)</label>
            <input
              type="number"
              step="0.1"
              id="weight"
              name="weight"
              className="form-control"
              value={formValues.weight || ''}
              onChange={(e) => setFormValues({...formValues, weight: e.target.value})}
              min="0"
            />
          </div>

          <div className="form-group form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="shipping_available"
              name="shipping_available"
              checked={formValues.shipping_available}
              onChange={(e) => setFormValues({...formValues, shipping_available: e.target.checked})}
            />
            <label className="form-check-label" htmlFor="shipping_available">
              Versand möglich
            </label>
          </div>

          <div className="form-group form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="pickup_preferred"
              name="pickup_preferred"
              checked={formValues.pickup_preferred}
              onChange={(e) => setFormValues({...formValues, pickup_preferred: e.target.checked})}
            />
            <label className="form-check-label" htmlFor="pickup_preferred">
              Abholung bevorzugt
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">payments</span>
            Zahlungsmethoden
          </h2>
          
          <div className="form-group">
            <label>Akzeptierte Zahlungsmethoden</label>
            <div className="payment-methods-container">
              <label 
                className={`payment-method-button ${formValues.payment_methods?.cash ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  name="payment_methods.cash"
                  checked={formValues.payment_methods?.cash || false}
                  onChange={handlePaymentMethodChange}
                  style={{display: 'none'}}
                />
                <span className="material-symbols-outlined">payments</span>
                Barzahlung
              </label>
              
              <label 
                className={`payment-method-button ${formValues.payment_methods?.bank_transfer ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  name="payment_methods.bank_transfer"
                  checked={formValues.payment_methods?.bank_transfer || false}
                  onChange={handlePaymentMethodChange}
                  style={{display: 'none'}}
                />
                <span className="material-symbols-outlined">account_balance</span>
                Banküberweisung
              </label>
              
              <label 
                className={`payment-method-button ${formValues.payment_methods?.paypal ? 'selected' : ''}`}
              >
                <input
                  type="checkbox"
                  name="payment_methods.paypal"
                  checked={formValues.payment_methods?.paypal || false}
                  onChange={handlePaymentMethodChange}
                  style={{display: 'none'}}
                />
                <span className="material-symbols-outlined">credit_card</span>
                PayPal
              </label>
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">location_on</span>
            Standort
          </h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="city">Stadt <span className="required">*</span></label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                placeholder="z.B. München"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="zip">Postleitzahl <span className="required">*</span></label>
              <input
                type="text"
                id="zip"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                required
                placeholder="z.B. 80331"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="country_code">Ländercode <span className="required">*</span></label>
              <input
                type="text"
                id="country_code"
                name="country_code"
                value={formData.country_code}
                onChange={handleInputChange}
                required
                maxLength="2"
                placeholder="DE"
              />
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">image</span>
            Bilder
          </h2>
          
          {isEditing && images.length > 0 && (
            <div className="current-images">
              <h3>Aktuelle Bilder</h3>
              <div className="image-gallery">
                {images.map(image => (
                  <div key={image.id} className="image-item">
                    <img src={image.image_path} alt="Listing" />
                    {images.length > 1 && (
                      <button 
                        type="button"
                        onClick={() => handleDeleteImage(image.id)}
                        className="delete-image-button"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="images">Bilder</label>
            <div className="file-upload-container">
              <input
                type="file"
                id="images"
                name="images"
                className="form-control-file"
                onChange={handleImageChange}
                multiple
                accept="image/*"
              />
              <label htmlFor="images" className="custom-file-upload">
                <span className="material-symbols-outlined">add_photo_alternate</span>
                Bilder hochladen
              </label>
            </div>
            
            {/* Vorhandene und neue Bilder anzeigen */}
            {(existingImages.length > 0 || newImages.length > 0) && renderImagePreviews()}
          </div>
        </div>
        
        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">security</span>
            Privatsphäre
          </h2>
          
          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="is_private"
                checked={formData.is_private}
                onChange={handleInputChange}
              />
              Privates Angebot (nur für registrierte Benutzer sichtbar)
            </label>
          </div>
        </div>

        <div className="form-section">
          <h2>
            <span className="material-symbols-outlined">contact_phone</span>
            Kontakteinstellungen
          </h2>
          
          <div className="form-group">
            <label>Telefonnummer anzeigen:</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="show_phone_to"
                  value="N"
                  checked={formData.show_phone_to === 'N'}
                  onChange={handleInputChange}
                />
                <span className="material-symbols-outlined">visibility_off</span>
                Nicht anzeigen
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="show_phone_to"
                  value="R"
                  checked={formData.show_phone_to === 'R'}
                  onChange={handleInputChange}
                />
                <span className="material-symbols-outlined">person</span>
                Nur für registrierte Benutzer
              </label>
              
              <label className="radio-label">
                <input
                  type="radio"
                  name="show_phone_to"
                  value="A"
                  checked={formData.show_phone_to === 'A'}
                  onChange={handleInputChange}
                />
                <span className="material-symbols-outlined">public</span>
                Für alle sichtbar
              </label>
            </div>
            
            {formData.show_phone_to === 'A' && (
              <div className="phone-warning">
                <span className="material-symbols-outlined">warning</span>
                <p>Achtung: Ihre Telefonnummer wird öffentlich für alle Besucher sichtbar sein.</p>
              </div>
            )}
          </div>
        </div>
        
        <div className="form-preview-section">
          <h3>Hinweis zum Angebot</h3>
          <div className="disclaimer-preview">
            <span className="material-symbols-outlined">info</span>
            <p>
              Zu Ihrem Angebot wird automatisch folgender Hinweis hinzugefügt:<br/>
              <strong>Privatverkauf: Ich schließe jegliche Sachmangelhaftung aus!</strong>
            </p>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <div className="loading-spinner small"></div>
                <span>Wird gespeichert...</span>
              </>
            ) : (
              <>
                <span className="material-symbols-outlined">save</span>
                <span>{isEditing ? 'Änderungen speichern' : 'Angebot erstellen'}</span>
              </>
            )}
          </button>
          
          <Link to={isEditing ? `/marketplace/${id}` : '/marketplace'} className="cancel-button">
            <span className="material-symbols-outlined">cancel</span>
            Abbrechen
          </Link>
        </div>
      </form>
    </div>
  );
};

export default MarketplaceForm;